/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { permissionsQuery } from '@/api/auth';
import { usePreferences } from '@/api/preferences';
import { Bell } from '@/components/Bell';
import { CommandPalette } from '@/components/CommandPalette';
import { StartAgreement } from '@/components/StartAgreement';
import { StartPacket } from '@/components/StartPacket';
import { Icon } from '@/components/ui/Icon';
import { Menu, MenuItem } from '@/components/ui/Menu';

/**
 * The top bar: find something, see what is waiting, start something new.
 *
 * The theme lives here because it is the one setting a person changes while
 * looking at the thing it changes. It is stored with the person like every
 * other preference, and mirrored into this browser so the sign-in screen —
 * which paints before anybody is authenticated — does not flash the wrong one.
 */
export function TopBar() {
  const navigate = useNavigate();
  const { prefs, set } = usePreferences();
  const { data: permissions } = useQuery(permissionsQuery());
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startingPacket, setStartingPacket] = useState(false);

  const dark = prefs.theme === 'dark';
  const canCreate = permissions?.has('agreement:create') ?? false;
  const canImport = permissions?.has('import:run') ?? false;
  const canManage = permissions?.has('workflow:manage') ?? false;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearching(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <header className="top">
      <div className="top-l">
        <button
          type="button"
          className="search"
          onClick={() => {
            setSearching(true);
          }}
          aria-label="Search Vellum"
          aria-keyshortcuts="Meta+K Control+K"
        >
          <Icon name="search" size={15} />
          <span>Search agreements, workflows, people and help</span>
          <kbd aria-hidden>⌘K</kbd>
        </button>
      </div>

      <div className="top-c" />

      <div className="tools">
        <button
          type="button"
          className="ib"
          onClick={() => {
            set({ theme: dark ? 'light' : 'dark' });
          }}
          aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-pressed={dark}
        >
          <Icon name={dark ? 'sun' : 'moon'} size={16} />
        </button>

        <Bell />

        <Menu
          label="Start something new"
          trigger={(props) => (
            <button type="button" className="btn btn-p" {...props}>
              <Icon name="plus" size={14} />
              New
            </button>
          )}
        >
          {(close) => (
            <>
              <MenuItem
                icon={<Icon name="doc" size={15} />}
                title="Start an agreement"
                detail="Pick a template and fill in the blanks"
                onClick={() => {
                  close();
                  // The same picker "New agreement" opens, so there is one way
                  // to start an agreement wherever somebody reaches for it.
                  setStarting(true);
                }}
              />
              <MenuItem
                icon={<Icon name="inbox" size={15} />}
                title="Start a document packet"
                detail="A checklist of everything one person has to sign"
                onClick={() => {
                  close();
                  setStartingPacket(true);
                }}
              />
              {canImport ? (
                <MenuItem
                  icon={<Icon name="down2" size={15} />}
                  title="Bring in signed contracts"
                  detail="File agreements that were signed before Vellum"
                  onClick={() => {
                    close();
                    void navigate({ to: '/import' });
                  }}
                />
              ) : null}
              {!canCreate ? (
                <p className="mut menu-note">
                  Your role does not include starting an agreement.
                </p>
              ) : null}
            </>
          )}
        </Menu>
      </div>

      {starting ? (
        <StartAgreement
          canManage={canManage}
          onClose={() => {
            setStarting(false);
          }}
        />
      ) : null}

      {startingPacket ? (
        <StartPacket
          onClose={() => {
            setStartingPacket(false);
          }}
        />
      ) : null}

      {searching ? (
        <CommandPalette
          onClose={() => {
            setSearching(false);
          }}
        />
      ) : null}
    </header>
  );
}
